// /src/pages/SettingsPage.js
import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavbar } from '../context/NavbarContext';
import { 
  NavigateNext,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

// Styled components
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: {
    xs: theme.spacing(1.5),
    sm: theme.spacing(2),
    md: theme.spacing(2)
  },
  textAlign: 'left',
  color: theme.palette.text.secondary,
  borderRadius: '8px',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: {
    xs: '12px',
    sm: '14px',
    md: '16px'
  },
}));

const SettingsPage = () => {
  const { isNavbarOpen } = useNavbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedSetting, setSelectedSetting] = React.useState(null);

  const handleOpenDialog = (setting = null) => {
    setSelectedSetting(setting);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSetting(null);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh', 
      width: '100%', 
      background: '#F8FAFF',
      margin: 0,
      padding: 0,
      overflow: 'auto',
    }}>
      <Container
        disableGutters
        sx={{
          minHeight: '100vh',
          width: '100%',
          flexGrow: 1,
          padding: {
            xs: '12px',
            sm: '16px',
            md: '20px',
            lg: '24px',
            xl: '32px'
          },
          marginLeft: {
            xs: isNavbarOpen ? '240px' : '70px',
            sm: isNavbarOpen ? '240px' : '70px',
            md: isNavbarOpen ? '240px' : '70px',
            lg: isNavbarOpen ? '240px' : '70px',
            xl: isNavbarOpen ? '240px' : '70px'
          },
          marginTop: {
            xs: '56px',
            sm: '64px',
            md: '64px',
            lg: '64px',
            xl: '64px'
          },
          transition: 'margin-left 0.3s, padding 0.3s',
          position: 'relative',
          maxWidth: '100% !important',
          overflow: 'auto',
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          gap: {
            xs: '12px',
            sm: '14px',
            md: '16px'
          },
          margin: 0,
          padding: 0,
        }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{
              '& .MuiBreadcrumbs-separator': {
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  md: '1rem'
                }
              }
            }}
          >
            <Link 
              underline="hover" 
              color="inherit" 
              href="/dashboard"
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
            Dashboard
          </Link>
            <Typography 
              color="text.primary"
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Settings
            </Typography>
        </Breadcrumbs>

          <Typography 
            variant="h4" 
            gutterBottom 
            fontWeight="600"
            sx={{ 
              color: '#1A1F36',
              letterSpacing: '-0.5px',
              position: 'relative',
              marginTop: 0,
              fontSize: {
                xs: '1.5rem',
                sm: '1.75rem',
                md: '2rem',
                lg: '2.25rem',
                xl: '2.5rem'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: '60px',
                height: '4px',
                backgroundColor: '#2196F3',
                borderRadius: '2px',
              }
            }}
          >
            System Settings
          </Typography>

          {/* Settings Categories */}
          <Grid container spacing={{ xs: 2, sm: 2, md: 2 }}>
            <Grid item xs={12} md={6}>
              <Item>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: {
                    xs: 1.5,
                    sm: 2,
                    md: 2
                  }
                }}>
                  <SettingsIcon sx={{ 
                    color: '#2196F3',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '1.75rem',
                      md: '2rem'
                    }
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '1.25rem',
                        sm: '1.5rem',
                        md: '1.75rem'
                      }
                    }}
                  >
                    General Settings
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="System Name"
                      secondary="Water Treatment Facility"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'System Name', value: 'Water Treatment Facility' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Time Zone"
                      secondary="UTC+5:30 (IST)"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'Time Zone', value: 'UTC+5:30 (IST)' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Date Format"
                      secondary="DD/MM/YYYY"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'Date Format', value: 'DD/MM/YYYY' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Item>
            </Grid>

            {/* Security Settings */}
            <Grid item xs={12} md={6}>
              <Item>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: {
                    xs: 1.5,
                    sm: 2,
                    md: 2
                  }
                }}>
                  <SecurityIcon sx={{ 
                    color: '#4CAF50',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '1.75rem',
                      md: '2rem'
                    }
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '1.25rem',
                        sm: '1.5rem',
                        md: '1.75rem'
                      }
                    }}
                  >
                    Security Settings
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Two-Factor Authentication"
                      secondary="Enabled"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked size={isMobile ? "small" : "medium"} />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Session Timeout"
                      secondary="30 minutes"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'Session Timeout', value: '30 minutes' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Password Policy"
                      secondary="Strong"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'Password Policy', value: 'Strong' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Item>
            </Grid>

            {/* Notification Settings */}
            <Grid item xs={12} md={6}>
              <Item>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: {
                    xs: 1.5,
                    sm: 2,
                    md: 2
                  }
                }}>
                  <NotificationsIcon sx={{ 
                    color: '#FF9800',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '1.75rem',
                      md: '2rem'
                    }
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '1.25rem',
                        sm: '1.5rem',
                        md: '1.75rem'
                      }
                    }}
                  >
                    Notification Settings
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Email Notifications"
                      secondary="Enabled"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked size={isMobile ? "small" : "medium"} />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="SMS Alerts"
                      secondary="Enabled"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked size={isMobile ? "small" : "medium"} />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Push Notifications"
                      secondary="Enabled"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked size={isMobile ? "small" : "medium"} />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Item>
            </Grid>

            {/* Backup Settings */}
            <Grid item xs={12} md={6}>
              <Item>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  mb: {
                    xs: 1.5,
                    sm: 2,
                    md: 2
                  }
                }}>
                  <BackupIcon sx={{ 
                    color: '#9C27B0',
                    fontSize: {
                      xs: '1.5rem',
                      sm: '1.75rem',
                      md: '2rem'
                    }
                  }} />
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#1A1F36', 
                      fontWeight: '600',
                      fontSize: {
                        xs: '1.25rem',
                        sm: '1.5rem',
                        md: '1.75rem'
                      }
                    }}
                  >
                    Backup Settings
                  </Typography>
                </Box>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Auto Backup"
                      secondary="Daily at 2:00 AM"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked size={isMobile ? "small" : "medium"} />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Backup Retention"
                      secondary="30 days"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size={isMobile ? "small" : "medium"}
                        onClick={() => handleOpenDialog({ name: 'Backup Retention', value: '30 days' })}
                      >
                        <EditIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Storage Usage"
                      secondary="1.2 TB of 2 TB"
                      primaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1rem'
                          }
                        }
                      }}
                      secondaryTypographyProps={{
                        sx: {
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Chip 
                        label="60%" 
                        color="primary" 
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '0.875rem'
                          }
                        }}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </Item>
            </Grid>
          </Grid>
        </Box>

        {/* Settings Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              m: { xs: 2, sm: 4 },
              width: {
                xs: '90%',
                sm: '500px',
                md: '600px'
              }
            }
          }}
        >
          <DialogTitle sx={{ 
            fontSize: {
              xs: '1.25rem',
              sm: '1.5rem',
              md: '1.75rem'
            }
          }}>
            Edit Setting
          </DialogTitle>
          <DialogContent>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              mt: 2 
            }}>
              <TextField
                label={selectedSetting?.name || ''}
                defaultValue={selectedSetting?.value || ''}
                fullWidth
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  },
                  '& .MuiInputBase-root': {
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                      md: '1rem'
                    }
                  }
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{
                  fontSize: {
                    xs: '0.875rem',
                    sm: '1rem',
                    md: '1rem'
                  }
                }}>
                  Type
                </InputLabel>
                <Select 
                  defaultValue="text" 
                  label="Type"
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                        md: '1rem'
                      }
                    }
                  }}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="select">Select</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCloseDialog} 
              startIcon={<CancelIcon />}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCloseDialog} 
              startIcon={<SaveIcon />}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1rem'
                }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default SettingsPage;
